import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, Chip, Stack, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Autocomplete, ToggleButton, ToggleButtonGroup, Alert, CircularProgress,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Title as ChartTitle, Tooltip as ChartTooltip, Legend as ChartLegend,
} from 'chart.js';
import { gicService } from '../services/api';
import { GIC, GicSummary, GicMaturityAlerts, GicAnalysis } from '../types';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTitle, ChartTooltip, ChartLegend);

type ChipColor = 'default' | 'success' | 'warning' | 'error';

const STATUS_COLOR: Record<string, ChipColor> = { active: 'success', matured: 'warning', redeemed: 'default' };

const formatMoney = (value: number | string, currency: string) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(num || 0);
};

const emptyForm = {
  bank_name: '', principal_amount: '', interest_rate: '', compounding_frequency: 'simple' as GIC['compounding_frequency'],
  term_value: '12', term_unit: 'months' as 'months' | 'years', start_date: new Date().toISOString().slice(0, 10),
  currency: 'CAD' as GIC['currency'], status: 'active' as GIC['status'], notes: '',
};

const addMonths = (dateStr: string, months: number) => {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const GICPage = () => {
  const [gics, setGics] = useState<GIC[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState<string[]>([]);

  const [summary, setSummary] = useState<GicSummary | null>(null);
  const [alerts, setAlerts] = useState<GicMaturityAlerts | null>(null);

  const [analysis, setAnalysis] = useState<GicAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<GIC | null>(null);

  const bankChartRef = useRef<ChartJS<'bar'> | null>(null);
  const currencyChartRef = useRef<ChartJS<'doughnut'> | null>(null);
  const rateChartRef = useRef<ChartJS<'bar'> | null>(null);

  const fetchGics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await gicService.getAll({ limit: rowsPerPage, offset: page * rowsPerPage });
      setGics(response.data);
      const allResponse = await gicService.getAll();
      setTotal(allResponse.data.length);
    } catch (error) {
      console.error('Error fetching GICs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  const fetchSideData = useCallback(async () => {
    try {
      const [summaryRes, alertsRes, banksRes] = await Promise.all([
        gicService.getSummary(), gicService.getMaturityAlerts('30,60,90'), gicService.getBanks(),
      ]);
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      console.error('Error fetching GIC summary data:', error);
    }
  }, []);

  useEffect(() => { fetchGics(); }, [fetchGics]);
  useEffect(() => { fetchSideData(); }, [fetchSideData]);

  const refreshAll = () => { fetchGics(); fetchSideData(); };

  const openCreateDialog = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };

  const openEditDialog = (gic: GIC) => {
    const termYears = gic.term_months % 12 === 0 && gic.term_months >= 12;
    setEditingId(gic.id);
    setForm({
      bank_name: gic.bank_name, principal_amount: gic.principal_amount, interest_rate: gic.interest_rate,
      compounding_frequency: gic.compounding_frequency,
      term_value: String(termYears ? gic.term_months / 12 : gic.term_months),
      term_unit: termYears ? 'years' : 'months',
      start_date: gic.start_date.slice(0, 10), currency: gic.currency, status: gic.status, notes: gic.notes || '',
    });
    setDialogOpen(true);
  };

  const termMonths = () => {
    const value = parseInt(form.term_value, 10) || 0;
    return form.term_unit === 'years' ? value * 12 : value;
  };

  const handleSave = async () => {
    const payload = {
      bank_name: form.bank_name.trim(),
      principal_amount: form.principal_amount,
      interest_rate: form.interest_rate,
      compounding_frequency: form.compounding_frequency,
      term_months: termMonths(),
      start_date: form.start_date,
      maturity_date: addMonths(form.start_date, termMonths()),
      currency: form.currency,
      status: form.status,
      notes: form.notes,
    };
    try {
      if (editingId) {
        await gicService.update(editingId, payload);
      } else {
        await gicService.create(payload);
      }
      setDialogOpen(false);
      refreshAll();
    } catch (error) {
      console.error('Error saving GIC:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await gicService.delete(deleteTarget.id);
      setDeleteTarget(null);
      refreshAll();
    } catch (error) {
      console.error('Error deleting GIC:', error);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const response = await gicService.analyze();
      setAnalysis(response.data);
    } catch (error: any) {
      setAnalysisError(error?.message || 'Failed to generate AI insights');
    } finally {
      setAnalyzing(false);
    }
  };

  const exportCSV = async () => {
    const response = await gicService.exportCSV();
    const url = window.URL.createObjectURL(new Blob([response.data as any]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `gics_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('GIC Portfolio Report', 14, 14);
    let chartsY = 20;
    if (bankChartRef.current) { doc.addImage(bankChartRef.current.toBase64Image(), 'PNG', 14, chartsY, 90, 60); }
    if (currencyChartRef.current) { doc.addImage(currencyChartRef.current.toBase64Image(), 'PNG', 110, chartsY, 90, 60); }
    chartsY += 65;
    const headers = ['Bank', 'Principal', 'Rate', 'Term (mo)', 'Maturity', 'Currency', 'Status'];
    const rows = gics.map((g) => [
      g.bank_name, g.principal_amount, `${g.interest_rate}%`, String(g.term_months),
      g.maturity_date.slice(0, 10), g.currency, g.status,
    ]);
    autoTable(doc, { head: [headers], body: rows, startY: chartsY });
    doc.save('gic-portfolio.pdf');
  };

  const totalPortfolioValue = summary
    ? Object.values(summary.totals.by_currency).reduce((sum, v) => sum + v.projected_value, 0)
    : 0;
  const totalProjectedInterest = summary
    ? Object.values(summary.totals.by_currency).reduce((sum, v) => sum + v.projected_interest, 0)
    : 0;

  const alertSeverity = alerts && (alerts.counts['30'] || 0) > 0 ? 'warning' : 'info';
  const alertText = alerts
    ? Object.entries(alerts.counts).map(([window, count]) => `${count} in ${window} days`).join(' · ')
    : '';

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>Bank GICs</Typography>
            <Typography variant="body1" color="text.secondary">Track your Guaranteed Investment Certificates</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>Add GIC</Button>
        </Stack>

        {alerts && alerts.gics.length > 0 && (
          <Alert severity={alertSeverity} sx={{ mb: 3 }}>Upcoming maturities: {alertText}</Alert>
        )}

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">Total GICs</Typography>
              <Typography variant="h5">{summary?.totals.count ?? 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">Projected Portfolio Value</Typography>
              <Typography variant="h5">{formatMoney(totalPortfolioValue, 'CAD')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">Projected Interest Earnings</Typography>
              <Typography variant="h5">{formatMoney(totalProjectedInterest, 'CAD')}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="subtitle2" gutterBottom>Principal by Bank</Typography>
              <Bar
                ref={bankChartRef}
                data={{
                  labels: summary?.by_bank.map((b) => b.bank_name) || [],
                  datasets: [{ label: 'Principal', data: summary?.by_bank.map((b) => b.principal) || [], backgroundColor: '#c9a84c' }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="subtitle2" gutterBottom>Breakdown by Currency</Typography>
              <Doughnut
                ref={currencyChartRef}
                data={{
                  labels: summary?.by_currency.map((c) => c.currency) || [],
                  datasets: [{ data: summary?.by_currency.map((c) => c.principal) || [], backgroundColor: ['#1a1a2e', '#c9a84c', '#4a7c6f'] }],
                }}
                options={{ responsive: true, maintainAspectRatio: false }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="subtitle2" gutterBottom>Maturity Timeline</Typography>
              <Bar
                data={{
                  labels: summary?.maturity_timeline.map((t) => t.month) || [],
                  datasets: [{ label: 'Maturing Principal', data: summary?.maturity_timeline.map((t) => t.maturing_principal) || [], backgroundColor: '#4a7c6f' }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="subtitle2" gutterBottom>Rate Comparison</Typography>
              <Bar
                ref={rateChartRef}
                data={{
                  labels: summary?.rate_comparison.map((r) => `${r.bank_name}`) || [],
                  datasets: [{ label: 'Interest Rate (%)', data: summary?.rate_comparison.map((r) => r.interest_rate) || [], backgroundColor: '#c0533a' }],
                }}
                options={{ indexAxis: 'y' as const, responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">AI Portfolio Insights</Typography>
            <Button variant="outlined" startIcon={analyzing ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              onClick={runAnalysis} disabled={analyzing}>
              {analyzing ? 'Analyzing...' : 'Generate Insights'}
            </Button>
          </Stack>

          {analysisError && <Alert severity="error">{analysisError}</Alert>}

          {analysis && !analysis.analyzed && (
            <Typography variant="body2" color="text.secondary">{analysis.message || 'No data available yet.'}</Typography>
          )}

          {analysis?.analyzed && analysis.insights && (
            <Stack spacing={2}>
              <Typography variant="body1">{analysis.insights.best_rate_summary}</Typography>
              {analysis.insights.top_bank_by_rate && (
                <Chip color="secondary" label={`Best rate: ${analysis.insights.top_bank_by_rate.bank_name} @ ${analysis.insights.top_bank_by_rate.interest_rate}%`} />
              )}
              {analysis.insights.laddering_suggestions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Laddering Suggestions</Typography>
                  <ul>{analysis.insights.laddering_suggestions.map((s, i) => <li key={i}><Typography variant="body2">{s}</Typography></li>)}</ul>
                </Box>
              )}
              {analysis.insights.diversification_notes && <Alert severity="info">{analysis.insights.diversification_notes}</Alert>}
              {analysis.insights.risk_notes && <Alert severity="warning">{analysis.insights.risk_notes}</Alert>}
              {analysis.insights.general_insights.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">General Insights</Typography>
                  <ul>{analysis.insights.general_insights.map((s, i) => <li key={i}><Typography variant="body2">{s}</Typography></li>)}</ul>
                </Box>
              )}
            </Stack>
          )}
        </Paper>

        <Stack direction="row" spacing={2} mb={2}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}>Export CSV</Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportPDF}>Export PDF</Button>
        </Stack>

        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bank</TableCell>
                <TableCell>Principal</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Maturity Date</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>Loading...</TableCell></TableRow>
              ) : gics.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>No GICs yet — add your first one above.</TableCell></TableRow>
              ) : gics.map((gic) => (
                <TableRow key={gic.id} hover>
                  <TableCell>{gic.bank_name}</TableCell>
                  <TableCell>{formatMoney(gic.principal_amount, gic.currency)}</TableCell>
                  <TableCell>{gic.interest_rate}%</TableCell>
                  <TableCell>{gic.term_months} mo</TableCell>
                  <TableCell>{gic.maturity_date.slice(0, 10)}</TableCell>
                  <TableCell>{gic.currency}</TableCell>
                  <TableCell><Chip size="small" label={gic.status} color={STATUS_COLOR[gic.status]} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditDialog(gic)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(gic)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit GIC' : 'Add GIC'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              freeSolo options={banks} value={form.bank_name}
              onInputChange={(_, value) => setForm((f) => ({ ...f, bank_name: value }))}
              renderInput={(params) => <TextField {...params} label="Bank Name" required />}
            />
            <Stack direction="row" spacing={2}>
              <TextField label="Principal Amount" type="number" fullWidth required
                value={form.principal_amount} onChange={(e) => setForm((f) => ({ ...f, principal_amount: e.target.value }))} />
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select label="Currency" value={form.currency}
                  onChange={(e: SelectChangeEvent) => setForm((f) => ({ ...f, currency: e.target.value as GIC['currency'] }))}>
                  <MenuItem value="CAD">CAD</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Interest Rate (%)" type="number" fullWidth required
                value={form.interest_rate} onChange={(e) => setForm((f) => ({ ...f, interest_rate: e.target.value }))} />
              <FormControl fullWidth>
                <InputLabel>Compounding</InputLabel>
                <Select label="Compounding" value={form.compounding_frequency}
                  onChange={(e: SelectChangeEvent) => setForm((f) => ({ ...f, compounding_frequency: e.target.value as GIC['compounding_frequency'] }))}>
                  <MenuItem value="simple">Simple</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                  <MenuItem value="semi_annually">Semi-Annually</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="Term" type="number" fullWidth required
                value={form.term_value} onChange={(e) => setForm((f) => ({ ...f, term_value: e.target.value }))} />
              <ToggleButtonGroup exclusive value={form.term_unit}
                onChange={(_, value) => value && setForm((f) => ({ ...f, term_unit: value }))}>
                <ToggleButton value="months">Months</ToggleButton>
                <ToggleButton value="years">Years</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
            <Typography variant="caption" color="text.secondary">
              Maturity date: {addMonths(form.start_date, termMonths())}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status}
                onChange={(e: SelectChangeEvent) => setForm((f) => ({ ...f, status: e.target.value as GIC['status'] }))}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="matured">Matured</MenuItem>
                <MenuItem value="redeemed">Redeemed</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Notes" multiline rows={2} fullWidth
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={!form.bank_name.trim() || !form.principal_amount || !form.interest_rate || !form.term_value}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete GIC</DialogTitle>
        <DialogContent>
          <Typography>Delete the GIC at {deleteTarget?.bank_name}? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GICPage;
